/**
 * API Health Check Hook
 *
 * Periodically checks the backend API health and updates the connection status
 * in the UI store. This enables the LIVE/OFFLINE indicator in the header.
 */

import { useEffect, useRef, useCallback } from 'react'
import { useUIStore } from '@/store'
import { get } from '@/services/api'

const HEALTH_CHECK_INTERVAL = 30000 // 30 seconds
const HEALTH_CHECK_TIMEOUT = 15000 // 15s — was 5s but the API often takes >5s
                                   // when training is mid-epoch on a tight-RAM
                                   // Spoke; that caused the LIVE/OFFLINE flap
                                   // visible in the header (May 3 2026).
const HEALTH_FAILURES_BEFORE_OFFLINE = 2 // require N consecutive fails before
                                         // flipping the badge — a single
                                         // transient should not show OFFLINE.

interface HealthResponse {
  status: string
  version?: string
  uptime?: number
}

export function useApiHealthCheck() {
  const { setApiStatus, isLiveMode } = useUIStore()
  const intervalRef = useRef<NodeJS.Timeout | null>(null)
  const isMountedRef = useRef(true)
  const failureCountRef = useRef(0)

  const checkHealth = useCallback(async () => {
    if (!isMountedRef.current) return

    try {
      // Don't flip status to "connecting" on every poll — that briefly
      // shows the spinner each cycle. Stay on previous status until we have
      // a definitive result.

      // Create an abort controller for timeout
      const controller = new AbortController()
      const timeoutId = setTimeout(() => controller.abort(), HEALTH_CHECK_TIMEOUT)

      // Use the simpler /health/live endpoint which is more reliable
      // Falls back to /health if /health/live fails
      const response = await get<HealthResponse>('/health/live', {
        signal: controller.signal,
      })

      clearTimeout(timeoutId)

      if (!isMountedRef.current) return

      // Accept 'healthy', 'ok', 'alive', 'ready', or 'degraded' as valid statuses
      // 'degraded' means API is running but some components might be unavailable
      const validStatuses = ['healthy', 'ok', 'alive', 'ready', 'degraded']
      if (response?.status && validStatuses.includes(response.status)) {
        failureCountRef.current = 0
        setApiStatus('connected')
      } else if (response && Object.keys(response).length > 0) {
        // If we got any response, the API is running
        failureCountRef.current = 0
        setApiStatus('connected')
      } else {
        failureCountRef.current += 1
        if (failureCountRef.current >= HEALTH_FAILURES_BEFORE_OFFLINE) {
          setApiStatus('error', 'API returned unhealthy status')
        }
      }
    } catch (error) {
      if (!isMountedRef.current) return

      const errorMessage = error instanceof Error ? error.message : 'Connection failed'
      failureCountRef.current += 1

      // Only flip the visible badge after N consecutive failures so a single
      // transient (slow training step) doesn't blink the header to OFFLINE.
      if (failureCountRef.current < HEALTH_FAILURES_BEFORE_OFFLINE) return

      // Check if it's a network error or timeout
      if (errorMessage.includes('abort') || errorMessage.includes('timeout')) {
        setApiStatus('error', 'API request timed out')
      } else if (errorMessage.includes('Network') || errorMessage.includes('fetch')) {
        setApiStatus('disconnected', 'Unable to reach API server')
      } else {
        setApiStatus('error', errorMessage)
      }
    }
  }, [setApiStatus])

  // Initial check on mount
  useEffect(() => {
    isMountedRef.current = true
    checkHealth()

    return () => {
      isMountedRef.current = false
    }
  }, [checkHealth])

  // Periodic checks when live mode is enabled
  useEffect(() => {
    if (isLiveMode) {
      // Clear any existing interval
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
      }

      // Set up periodic health checks
      intervalRef.current = setInterval(checkHealth, HEALTH_CHECK_INTERVAL)

      return () => {
        if (intervalRef.current) {
          clearInterval(intervalRef.current)
          intervalRef.current = null
        }
      }
    } else {
      // Clear interval when live mode is disabled
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }
    }
  }, [isLiveMode, checkHealth])

  // Manual refresh function
  const refreshStatus = useCallback(() => {
    checkHealth()
  }, [checkHealth])

  return { refreshStatus }
}

export default useApiHealthCheck
